// Empty / error / offline / "source unavailable" states in Orca's language:
// a quiet sonar mark (rings + icon), serif title, one clear action.
import i18n from '@/i18n';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { cx } from '@/lib/format';
import { XButton } from './Buttons';
import { Icon } from './Icon';

export type StateKind = 'empty' | 'error' | 'offline' | 'source' | 'success' | 'paused';
type Action = { label: string; to?: string; onClick?: () => void; busy?: boolean };

const KIND_ICON: Record<StateKind, string> = { empty: 'radar', error: 'alert', offline: 'globe', source: 'refresh', success: 'check', paused: 'bell' };

export function StateCard({ kind = 'empty', icon, title, text, action, second, compact, className, children, onLight = false }: {
  kind?: StateKind; icon?: string; title: ReactNode; text?: ReactNode; action?: Action; second?: Action; compact?: boolean; className?: string; children?: ReactNode;
  /** on the light hero panel (page error): dark button instead of the light one */
  onLight?: boolean;
}) {
  return (
    <div className={cx('stc', kind, compact && 'compact', className)} role={kind === 'error' || kind === 'offline' ? 'alert' : undefined}>
      <span className="stc-mark" aria-hidden="true"><i /><i /><i /><b><Icon name={icon ?? KIND_ICON[kind]} /></b></span>
      <div className="stc-t">
        <h4>{title}</h4>
        {text && <p>{text}</p>}
        {children}
        {(action || second) && (
          <div className="stc-acts">
            {action && <XButton label={action.label} to={action.to} onClick={action.onClick} busy={action.busy} sm inv={!onLight} />}
            {second && (second.to ? <Link className="stc-link" to={second.to}>{second.label}</Link> : <button type="button" className="stc-link" onClick={second.onClick}>{second.label}</button>)}
          </div>
        )}
      </div>
    </div>
  );
}

/** Thin warning strip inside a block: one source is down, the rest still works. */
export function SourceBanner({ children, onRetry }: { children: ReactNode; onRetry?: () => void }) {
  const [busy, setBusy] = useState(false);
  return (
    <div className="src-ban" role="status">
      <span className="src-dot" aria-hidden="true" />
      <p>{children}</p>
      {onRetry && <button type="button" className={cx(busy && 'busy')} onClick={() => { setBusy(true); window.setTimeout(() => { setBusy(false); onRetry(); }, 900); }}><Icon name="refresh" />{busy ? i18n.t('common:action.checking') : i18n.t('nav:check')}</button>}
    </div>
  );
}

/** A block that failed on its own; «Повторить» retries just this block (mock: succeeds). */
export function BlockError({ what, children, fail = true }: { what: string; children: ReactNode; fail?: boolean }) {
  const [state, setState] = useState<'error' | 'busy' | 'ok'>(fail ? 'error' : 'ok');
  if (state === 'ok') return <>{children}</>;
  return (
    <StateCard kind="error" compact title={i18n.t('common:state.loadFailed', { what })} text={i18n.t('common:state.loadFailedText')}
      action={{ label: state === 'busy' ? i18n.t('common:action.loading') : i18n.t('common:action.retry'), busy: state === 'busy', onClick: () => { setState('busy'); window.setTimeout(() => setState('ok'), 1100); } }} />
  );
}
